import { useState, useEffect, useMemo } from "react"
import { Plus, Trash2 } from "lucide-react"
import { cn } from "../../lib/utils"
import axios from "axios"
import { ToastContainer, toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
// import PDFUploader from "../../components/dashboard/PDFUploader"
import ValidationErrorSidebar from '../../components/ui/ValidationErrorSidebar'
import { validationPatterns as centralizedPatterns } from '../../utils/validationPatterns';
// import OtherTDSJSONUploader from '../../components/dashboard/OtherTDSJSONUploader'; // Removed JSON uploader
import { usePageSave } from '../../contexts/PageSaveContext';

// Placeholder for config, replace with your actual config import
import config from "../../config"

const tabs = [
  { id: "taxPaid", label: "Tax Paid" },
  { id: "tdsOnSalary", label: "TDS on salary" },
  { id: "otherTDS", label: "Other TDS" },
  { id: "otherTDS2", label: "Other TDS2" },
  { id: "tcs", label: "TCS" },
]

const columns = {
  taxPaid: [
    { id: "dateOfDeposit", label: "Date of Deposit", apiField: "dateOfDeposit" },
    { id: "bsrCode", label: "BSR Code", apiField: "bsrCode" },
    { id: "challanNo", label: "Challan No.", apiField: "serialNumberOfChallan" },
    { id: "amount", label: "Amount", prefix: "₹", apiField: "amount" },
  ],
  tdsOnSalary: [
    { id: "tan", label: "TAN", apiField: "tan" },
    { id: "nameOfEmployer", label: "Name of Employer", apiField: "name" },
    { id: "grossAmount", label: "Gross Amount", apiField: "incomeChargeableToSalary" },
    { id: "tds", label: "TDS", apiField: "TotalTdsOnSalary" },
  ],
  otherTDS: [
    { id: "selfTDS", label: "Self TDS", type: "checkbox", conditional: true },
    { id: "panWhoseTDSClaimed", label: "PAN (whose TDS claimed)", apiField: null, conditional: true },
    { id: "tanOfDeductor", label: "TAN of Deductor", apiField: "tan" },
    { id: "name", label: "Name", apiField: "name" },
    { id: "section", label: "Section", apiField: "tdsSection", options: true },
    { id: "finYear", label: "Fin. Year", apiField: "deductedYear", options: true },
    { id: "grossAmount", label: "Gross Amount", apiField: "amountForTaxDeduct" },
    { id: "tds", label: "TDS", apiField: "totalTdsOnAmountPaid" },
    { id: "selfClaimed", label: "Self claimed", type: "checkbox", conditional: true },
    { id: "tdsClaimed", label: "TDS Claimed", apiField: "claimOutOfTotalTdsOnAmountPaid" },
  ],
  otherTDS2: [
    { id: "selfTDS", label: "Self TDS", type: "checkbox", conditional: true }, // No direct API mapping for this UI checkbox
    { id: "panWhoseTDSClaimed", label: "PAN (whose TDS claimed)", apiField: "panWhoseTDSClaimed", conditional: true }, // New PAN field for TDS claimed
    { id: "name", label: "Name", apiField: "nameOfTenant" }, // Name field should always be visible
    { id: "panOfBuyerTenant", label: "PAN of Buyer/Tenant", apiField: "panOfTenant" },
    { id: "section", label: "Section", apiField: "tdsSection", options: true },
    { id: "finYear2", label: "Fin. Year", apiField: "deductedYear", options: true },
    { id: "grossAmount", label: "Gross Amount", apiField: "grsReceiptToTaxDeduct" },
    { id: "tds", label: "TDS", apiField: "tdsDeducted" },
    { id: "selfClaimed", label: "Self claimed", type: "checkbox", conditional: true }, // No direct API mapping for this UI checkbox
    { id: "tdsClaimed", label: "TDS Claimed", apiField: "tdsClaimed" },
  ],
  tcs: [
    { id: "selfTDS", label: "Self TDS", type: "checkbox", conditional: true }, // No direct API mapping for this UI checkbox
    { id: "panWhoseTCSClaimed", label: "PAN (whose TCS claimed)", apiField: "name", conditional: true }, // Assuming name maps to this
    { id: "tanOfCollector", label: "TAN of collector", apiField: "tan" },
    { id: "name", label: "Name", apiField: "name" }, // Name field - always visible
    { id: "tcsFinYear", label: "Fin. Year", apiField: "collectedYear", options: true },
    { id: "tcs", label: "TCS", apiField: "amountTaxCollected" },
    { id: "selfClaimed", label: "Self claimed", type: "checkbox", conditional: true }, // No direct API mapping for this UI checkbox
    { id: "tcsClaimed", label: "TCS Claimed", apiField: "amountTcsClaimedThisYear" },
  ],
}

const generateEmptyRow = (tabId, apiId = null) => {
  const fields = {}
  const row = {
    id: (Date.now().toString(36) + Math.random().toString(36).substring(2)),
    apiId: apiId,
  }

     columns[tabId].forEach((col) => {
     if (col.type === "checkbox") {
       row[col.id] = false
     } else {
       fields[col.id] = ""
       // Initialize specific fields
       if (col.id === "name") {
         fields[col.id] = ""
       }
       if (col.id === "panWhoseTDSClaimed") {
         fields[col.id] = ""
       }
       if (col.id === "panWhoseTCSClaimed") {
         fields[col.id] = ""
       }
       if (col.id === "tanOfCollector") {
         fields[col.id] = ""
       }
       if (col.id === "finYear") {
         fields[col.id] = ""
       }
       if (col.id === "tcsFinYear") {
         fields[col.id] = ""
       }
       if (col.id === "tcs") {
         fields[col.id] = ""
       }
       if (col.id === "tcsClaimed") {
         fields[col.id] = ""
       }
     }
   })
  row.fields = fields
  return row
}

const initialFormData = {
  taxPaid: [],
  tdsOnSalary: [],
  otherTDS: [],
  otherTDS2: [],
  tcs: [],
}

export default function TaxForm() {
  const { markPageAsSaved, markPageAsUnsaved } = usePageSave()
  const [activeTab, setActiveTab] = useState("taxPaid")
  const [formData, setFormData] = useState(initialFormData)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [taxPaidId, setTaxPaidId] = useState(null)
  const [validationErrors, setValidationErrors] = useState({})
       const [tdsClaimType, setTdsClaimType] = useState("Self") // Common TDS Claim Type for the page
  const [tdsClaimType2, setTdsClaimType2] = useState("Self") // TDS Claim Type for Other TDS2 tab
  const [tcsClaimType, setTcsClaimType] = useState("Self") // TCS Claim Type for TCS tab
  // const [showJSONUploader, setShowJSONUploader] = useState(false) // Removed JSON uploader state
  console.log("TaxPaidId:", taxPaidId)
   
       // Handle TDS Claim Type change and validate accordingly
    const handleTdsClaimTypeChange = (value) => {
      setTdsClaimType(value);
      
      // Clear validation errors for conditional fields when switching between types
      if (value === "Self") {
        // Clear errors for fields that are only required when "Others" is selected
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          Object.keys(newErrors).forEach(key => {
            if (key.includes('panWhoseTDSClaimed')) {
              delete newErrors[key];
            }
          });
          return newErrors;
        });
      } else if (value === "Others") {
        // Validate TDS Claimed field for all rows when switching to "Others"
        formData.otherTDS.forEach(row => {
          const tdsClaimedValue = row.fields.claimOutOfTotalTdsOnAmountPaid || row.fields.tdsClaimed || "";
          if (!tdsClaimedValue || tdsClaimedValue.trim() === "") {
            setValidationErrors(prev => ({ 
              ...prev, 
              [`${row.id}_tdsClaimed`]: "TDS Claimed is mandatory when schedule is filled" 
            }));
          }
        });
      }
    };

         // Handle TDS Claim Type 2 change for Other TDS2 tab
     const handleTdsClaimType2Change = (value) => {
       setTdsClaimType2(value);
       
       // Clear validation errors for conditional fields when switching between types
       if (value === "Self") {
         // Clear errors for fields that are only required when "Others" is selected
         setValidationErrors(prev => {
           const newErrors = { ...prev };
           Object.keys(newErrors).forEach(key => {
             if (key.includes('selfTDS') || key.includes('selfClaimed') || key.includes('panWhoseTDSClaimed')) {
               delete newErrors[key];
             }
           });
           return newErrors;
         });
       }
       // Note: 'name' field is no longer conditional, so we don't clear its validation errors
     };

    // Handle TCS Claim Type change for TCS tab
    const handleTcsClaimTypeChange = (value) => {
      setTcsClaimType(value);
      
      // Clear validation errors for conditional fields when switching between types
      if (value === "Self") {
        // Clear errors for fields that are only required when "Others" is selected
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          Object.keys(newErrors).forEach(key => {
            if (key.includes('panWhoseTCSClaimed')) {
              delete newErrors[key];
            }
          });
          return newErrors;
        });
      }
    };

  const validateClaimedAmount = (rowId, fieldId, value) => {
    const row = formData[activeTab].find(row => row.id === rowId);
    if (!row) return true; // if row not found, allow

    // Only validate for tdsClaimed and tcsClaimed fields
    if (fieldId === 'tdsClaimed' || fieldId === 'tcsClaimed') {
      const tdsOrTcsField = fieldId === 'tdsClaimed' ? 'tds' : 'tcs';
      const claimedAmount = parseFloat(value) || 0;
      const originalAmount = parseFloat(row.fields[tdsOrTcsField]) || 0;

      if (claimedAmount > originalAmount) {
        toast.error(`${fieldId === 'tdsClaimed' ? 'TDS' : 'TCS'} Claimed cannot be greater than ${fieldId === 'tdsClaimed' ? 'TDS' : 'TCS'} Amount`);
        return false;
      }
    }
    return true;
  };
  const fetchDeductionData = async () => {
    setIsLoading(true)
    try {
      const panNo = localStorage.getItem("PAN_NO")
      const year = localStorage.getItem("YEAR")
      if (!panNo || !year) {
        toast.error("PAN number or Year not found in local storage.")
        setIsLoading(false)
        return
      }

      const response = await axios.get(`${config.apiUrl}/taxpaid/${panNo}/${year}`)
      const apiData = response.data.taxPaid // Assuming the main payload is under taxPaidDto

      const newFormData = {}
      console.log("API Data:", apiData)
      setTaxPaidId(apiData?.taxPaId || null)

      // Map taxPaidDto.taxPayment to taxPaid tab
      newFormData.taxPaid = (apiData.taxPayment || []).map((item) => ({
        id: (Date.now().toString(36) + Math.random().toString(36).substring(2)), // Client-side ID
        apiId: item.taxPaymentId, // API ID
        fields: {
          dateOfDeposit: item.dateOfDeposit || "",
          bsrCode: item.bsrCode || "",
          challanNo: item.serialNumberOfChallan || "",
          amount: item.amount?.toString() || "",
        },
      }))
      // Ensure at least one empty row if no data
      if (newFormData.taxPaid.length === 0) {
        newFormData.taxPaid.push(generateEmptyRow("taxPaid"))
      }

      // Map tdsOnSalaryDto to tdsOnSalary tab
      newFormData.tdsOnSalary = (apiData.tdsOnSalaryDto || []).map((item) => ({
        id: (Date.now().toString(36) + Math.random().toString(36).substring(2)),
        apiId: item.tdsOnSalaryId,
        fields: {
          tan: item.tan || "",
          nameOfEmployer: item.name || "",
          grossAmount: item.incomeChargeableToSalary?.toString() || "",
          tds: item.TotalTdsOnSalary?.toString() || "",
        },
      }))
      if (newFormData.tdsOnSalary.length === 0) {
        newFormData.tdsOnSalary.push(generateEmptyRow("tdsOnSalary"))
      }

                    // Map tdsOnOtherThanSalaryTwos to otherTDS tab
        newFormData.otherTDS = (apiData.tdsOnOtherThanSalaryTwos || []).map((item) => ({
          id: (Date.now().toString(36) + Math.random().toString(36).substring(2)),
          apiId: item.tdsOnOtherThanSalaryTwoId,
          selfTDS: false, // Assuming default, as no direct mapping in API payload
          selfClaimed: false, // Assuming default
          fields: {
            name: item.name || "", // Use lowercase 'name' from API
            panWhoseTDSClaimed: "", // UI-only field, don't load from API
            tanOfDeductor: item.tan || "",
            section: item.tdsSection || "",
            finYear: item.deductedYear || "", // Correct: otherTDS uses finYear
            grossAmount: item.amountForTaxDeduct?.toString() || "",
            tds: item.totalTdsOnAmountPaid?.toString() || "",
            tdsClaimed: item.claimOutOfTotalTdsOnAmountPaid?.toString() || "",
          },
        }))
      if (newFormData.otherTDS.length === 0) {
        newFormData.otherTDS.push(generateEmptyRow("otherTDS"))
      }

      // Map tdsOnOtherThanSalaryThrees to otherTDS2 tab
      newFormData.otherTDS2 = (apiData.tdsOnOtherThanSalaryThrees || []).map((item) => ({
        id: (Date.now().toString(36) + Math.random().toString(36).substring(2)),
        apiId: item.tdsOnOtherThanSalaryThreeId,
        selfTDS: false, // Assuming default
        selfClaimed: false, // Assuming default
        fields: {
          name: item.nameOfTenant || "", // New name field
          panOfBuyerTenant: item.panOfTenant || "",
          section: item.tdsSection || "",
          finYear2: item.deductedYear || "", // Correct: otherTDS2 uses finYear2
          grossAmount: item.grsReceiptToTaxDeduct?.toString() || "",
          tds: item.tdsDeducted?.toString() || "",
          tdsClaimed: item.tdsClaimed?.toString() || "",
        },
      }))
      if (newFormData.otherTDS2.length === 0) {
        newFormData.otherTDS2.push(generateEmptyRow("otherTDS2"))
      }

      // Map tcss to tcs tab
      newFormData.tcs = (apiData.tcss || []).map((item) => {
        // Find existing TCS row with same apiId to preserve year value
        const existingRow = formData.tcs.find(row => row.apiId === item.tcsId);
        const preservedYear = existingRow?.fields?.tcsFinYear || item.collectedYear || "";
        
        return {
        id: (Date.now().toString(36) + Math.random().toString(36).substring(2)),
          apiId: item.tcsId, // Use correct field name 'tcsId'
        selfTDS: false, // Assuming default
        selfClaimed: false, // Assuming default
        fields: {
          panWhoseTCSClaimed: item.name || "", // Assuming name maps to this
          name: item.name || "", // New name field
          tanOfCollector: item.tan || "",
            tcsFinYear: preservedYear, // Preserve year value from existing data
            tcs: item.amountTaxCollected?.toString() || "", // Convert to string for consistency
            tcsClaimed: item.amountTcsClaimedThisYear?.toString() || "", // Convert to string for consistency
          },
        };
      })
      console.log('TCS data from API:', JSON.stringify(apiData.tcss, null, 2));
      console.log('TCS collectedYear values:', apiData.tcss?.map(item => ({ tcsId: item.tcsId, collectedYear: item.collectedYear })));
      console.log('Mapped TCS data:', JSON.stringify(newFormData.tcs, null, 2));
      console.log('TCS tcsFinYear values after mapping:', newFormData.tcs?.map(item => ({ id: item.id, tcsFinYear: item.fields.tcsFinYear })));
      
      if (newFormData.tcs.length === 0) {
        const emptyTcsRow = generateEmptyRow("tcs");
        console.log('Generated empty TCS row:', emptyTcsRow);
        newFormData.tcs.push(emptyTcsRow);
      }

      // Set API data directly (this is only called when no prefill data exists)
      setFormData(newFormData);
      
      toast.success("TDS data loaded successfully!")
    } catch (error) {
      console.error("Error fetching TDS data:", error)
      toast.error("Failed to load TDS data.")
      // Initialize with empty rows for all tabs on error
      const emptyFormData = {}
      tabs.forEach((tab) => {
        emptyFormData[tab.id] = [generateEmptyRow(tab.id)]
      })
      setFormData(emptyFormData)
    } finally {
      setIsLoading(false)
    }
  }


  useEffect(() => {
    const loadData = async () => {
      // Check if prefill data exists - if yes, load it and skip API call
      const prefillTaxPaid = localStorage.getItem("prefill_tax_paid");
      const prefillOtherTDS = localStorage.getItem("prefill_other_tds");
      const prefillTcs = localStorage.getItem("prefill_tcs");
      
      if (prefillTaxPaid || prefillOtherTDS || prefillTcs) {
        console.log('Prefill data found - loading from localStorage');
        
        // Initialize all tabs with empty rows first
        const initialFormData = {};
        tabs.forEach((tab) => {
          initialFormData[tab.id] = [generateEmptyRow(tab.id)];
        });
        
        // Load prefill data
        if (prefillTaxPaid) {
          try {
            const taxPaidData = JSON.parse(prefillTaxPaid);
            if (Array.isArray(taxPaidData) && taxPaidData.length > 0) {
              initialFormData.taxPaid = taxPaidData;
              console.log('Loaded prefill tax paid data:', taxPaidData.length, 'entries');
            }
          } catch (error) {
            console.error('Error loading prefill tax paid data:', error);
          }
        }
        
        if (prefillOtherTDS) {
          try {
            const otherTDSData = JSON.parse(prefillOtherTDS);
            if (Array.isArray(otherTDSData) && otherTDSData.length > 0) {
              initialFormData.otherTDS = otherTDSData;
              console.log('Loaded prefill other TDS data:', otherTDSData.length, 'entries');
            }
          } catch (error) {
            console.error('Error loading prefill other TDS data:', error);
          }
        }
        
        if (prefillTcs) {
          try {
            const tcsData = JSON.parse(prefillTcs);
            if (Array.isArray(tcsData) && tcsData.length > 0) {
              initialFormData.tcs = tcsData;
              console.log('Loaded prefill TCS data:', tcsData.length, 'entries');
            }
          } catch (error) {
            console.error('Error loading prefill TCS data:', error);
          }
        }
        
        setFormData(initialFormData);
        setIsLoading(false);
      } else {
        // No prefill data - fetch from API
        console.log('No prefill data found - fetching from API');
        await fetchDeductionData();
      }
    };
    
    loadData();
  }, []) // Run once on component mount

  // Listen for prefill data updates from project-level uploader
  useEffect(() => {
    const handlePrefillDataUpdate = (event) => {
      const prefillData = event.detail;
      
      // Always replace current data with prefill data when uploaded
      if (prefillData.taxPaid && prefillData.taxPaid.length > 0) {
        setFormData(prev => ({
          ...prev,
          taxPaid: [...prev.taxPaid.filter(row => 
            Object.values(row.fields || {}).some(value => value && value.toString().trim() !== "")
          ), ...prefillData.taxPaid]
        }));
        toast.success(`Loaded ${prefillData.taxPaid.length} Tax Paid entries from prefill data`);
      }
      
      if (prefillData.otherTDS && prefillData.otherTDS.length > 0) {
        setFormData(prev => ({
          ...prev,
          otherTDS: [...prev.otherTDS.filter(row => 
            Object.values(row.fields || {}).some(value => value && value.toString().trim() !== "")
          ), ...prefillData.otherTDS]
        }));
        toast.success(`Loaded ${prefillData.otherTDS.length} Other TDS entries from prefill data`);
      }

      if (prefillData.tcs && prefillData.tcs.length > 0) {
        console.log('TCS Prefill Data received:', prefillData.tcs);
        console.log('Current TCS form data before update:', formData.tcs);
        
        setFormData(prev => {
          const filteredExisting = prev.tcs.filter(row => 
            Object.values(row.fields || {}).some(value => value && value.toString().trim() !== "")
          );
          const updatedTcs = [...filteredExisting, ...prefillData.tcs];
          
          console.log('Filtered existing TCS data:', filteredExisting);
          console.log('Updated TCS data:', updatedTcs);
          
          return {
            ...prev,
            tcs: updatedTcs
          };
        });
        toast.success(`Loaded ${prefillData.tcs.length} TCS entries from prefill data`);
      } else {
        console.log('No TCS prefill data found:', prefillData.tcs);
      }
    };

    window.addEventListener('prefillDataUpdated', handlePrefillDataUpdate);
    
    return () => {
      window.removeEventListener('prefillDataUpdated', handlePrefillDataUpdate);
    };
  }, [])

  const [sectionList, setSectionList] = useState([])
  const [finalYearList, setFinalYearList] = useState([])
  const [finalYearList2, setFinalYearList2] = useState([])
  const optionsMap = useMemo(() => ({
    section: sectionList,
    finYear: finalYearList,
    finYear2: finalYearList2,
    tcsFinYear: finalYearList, // Use the same year list as otherTDS (TdsDeductedYr)
  }), [sectionList, finalYearList, finalYearList2]);
  const getAllCodeList = async (codeType, setDataFn) => {
    try {
      const response = await axios.get(`${config.apiUrl}/system/code/${codeType}`);
      const data = response.data;

      if (data && data.codes?.length > 0) {
        let sortedCodes = [...data.codes];
        
        // Sort financial year options in descending order
        if (codeType === "TdsDeductedYr" || codeType === "DeductedYr") {
          sortedCodes.sort((a, b) => {
            // Extract year from description (assuming format like "2024-25", "2023-24", etc.)
            const yearA = a.description?.split('-')[0] || '0';
            const yearB = b.description?.split('-')[0] || '0';
            return parseInt(yearB) - parseInt(yearA); // Descending order
          });
        }
        
        setDataFn(sortedCodes);
      } else {
        setDataFn([]); // empty array if no data
      }
    } catch (error) {
      console.log(`Error fetching ${codeType}:`, error);
      toast.error(`Failed to fetch ${codeType}.`);
    }
  };

  useEffect(() => {
    getAllCodeList("TDSSection", setSectionList);
    getAllCodeList("TdsDeductedYr", setFinalYearList);
    getAllCodeList("DeductedYr", setFinalYearList2);
    // TCS uses the same year list as otherTDS2 (DeductedYr)

  }, [])

  const handleInputChange = (rowId, fieldId, value) => {
    markPageAsUnsaved('tds-tcs')
    // Check if this is an amount field that needs validation
    const amountFields = ['amount', 'grossAmount', 'tds', 'tdsClaimed', 'tcs', 'tcsClaimed', 'amountForTaxDeduct', 'totalTdsOnAmountPaid', 'claimOutOfTotalTdsOnAmountPaid', 'grsReceiptToTaxDeduct', 'tdsDeducted', 'amountTaxCollected', 'amountTcsClaimedThisYear'];
    
    let processedValue = value;
    if (amountFields.includes(fieldId)) {
      // Only allow numbers (0-9) and decimal point
      processedValue = value.replace(/[^0-9.]/g, '');
      // Prevent multiple decimal points
      const parts = processedValue.split('.');
      processedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : processedValue;
      // Ensure non-negative
      const numericValue = Math.max(0, Number.parseFloat(processedValue || 0));
      processedValue = numericValue.toString();
    }

    // Always update the field value first (allow typing)
    setFormData((prev) => {
      const newData = { ...prev }
      const rows = [...newData[activeTab]]
      const rowIndex = rows.findIndex((row) => row.id === rowId)
      if (rowIndex !== -1) {
        rows[rowIndex] = {
          ...rows[rowIndex],
          fields: {
            ...rows[rowIndex].fields,
            [fieldId]: processedValue,
          },
        }
      }
      newData[activeTab] = rows
      return newData
    })

         // Validate PAN and TAN fields but don't block input
     if (fieldId === 'tan' || fieldId === 'tanOfDeductor' || fieldId === 'tanOfCollector') {
       if (value && value.trim()) {
         const tanPattern = centralizedPatterns.TAN;
         if (!tanPattern.test(value.trim())) {
           setValidationErrors(prev => ({
             ...prev,
             [`${rowId}_${fieldId}`]: "Invalid TAN format. Expected format: DELA12345F"
           }));
         } else {
           // Clear TAN validation error when format is correct
           clearValidationError(`${rowId}_${fieldId}`);
         }
       } else {
         // Clear error if field is empty
         clearValidationError(`${rowId}_${fieldId}`);
       }
     }
    
    if (fieldId === 'panWhoseTDSClaimed' || fieldId === 'panOfBuyerTenant' || fieldId === 'panWhoseTCSClaimed') {
      if (value && value.trim()) {
        const panPattern = centralizedPatterns.PAN;
        if (!panPattern.test(value.trim())) {
          setValidationErrors(prev => ({
            ...prev,
            [`${rowId}_${fieldId}`]: "Invalid PAN format. Expected format: ABCDE1234F"
          }));
        } else {
          setValidationErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[`${rowId}_${fieldId}`];
            return newErrors;
          });
        }
      } else {
        // Clear error if field is empty
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[`${rowId}_${fieldId}`];
          return newErrors;
        });
      }
    }

         // Validate claimed amount but don't block input
     if (!validateClaimedAmount(rowId, fieldId, value)) {
       // Don't return here - let the validation show as an error instead
     }
     
           // Real-time validation for Other TDS tab
      if (activeTab === 'otherTDS') {
        const row = formData[activeTab].find(row => row.id === rowId);
        if (row) {
          const fields = row.fields;
          
                    // Real-time TDS vs Gross Amount validation
           if (fieldId === 'grossAmount' || fieldId === 'tds' || fieldId === 'amountForTaxDeduct' || fieldId === 'totalTdsOnAmountPaid') {
             if (fields.grossAmount && fields.tds && fields.grossAmount.trim() !== "" && fields.tds.trim() !== "") {
               const grossAmount = parseFloat(fields.grossAmount) || 0;
               const tds = parseFloat(fields.tds) || 0;
               if (tds > grossAmount) {
                 setValidationErrors(prev => ({ ...prev, [`${rowId}_tds`]: "TDS cannot exceed Gross Amount" }));
               } else {
                 clearValidationError(`${rowId}_tds`);
               }
             }
           }
           
           // Real-time TDS Claimed vs TDS validation
           if (fieldId === 'tds' || fieldId === 'tdsClaimed' || fieldId === 'totalTdsOnAmountPaid' || fieldId === 'claimOutOfTotalTdsOnAmountPaid') {
             if (fields.tds && fields.tdsClaimed && fields.tds.trim() !== "" && fields.tdsClaimed.trim() !== "") {
               const tds = parseFloat(fields.tds) || 0;
               const tdsClaimed = parseFloat(fields.tdsClaimed) || 0;
               if (tdsClaimed > tds) {
                 setValidationErrors(prev => ({ ...prev, [`${rowId}_tdsClaimed`]: "TDS Claimed cannot exceed TDS Amount" }));
               } else {
                 clearValidationError(`${rowId}_tdsClaimed`);
               }
             }
           }
           
                      // Real-time validation for TDS Claimed - always mandatory when schedule is filled
            if (fieldId === 'tdsClaimed' || fieldId === 'claimOutOfTotalTdsOnAmountPaid') {
              if (!value || value.trim() === "") {
                setValidationErrors(prev => ({ ...prev, [`${rowId}_tdsClaimed`]: "TDS Claimed is mandatory when schedule is filled" }));
              } else {
                clearValidationError(`${rowId}_tdsClaimed`);
              }
            }
        }
      }
      
             // Real-time validation for Other TDS2 tab
       if (activeTab === 'otherTDS2') {
         const row = formData[activeTab].find(row => row.id === rowId);
         if (row) {
           const fields = row.fields;
           
           // Real-time TDS vs Gross Amount validation
           if (fieldId === 'grossAmount' || fieldId === 'tds' || fieldId === 'grsReceiptToTaxDeduct' || fieldId === 'tdsDeducted') {
             if (fields.grossAmount && fields.tds && fields.grossAmount.trim() !== "" && fields.tds.trim() !== "") {
               const grossAmount = parseFloat(fields.grossAmount) || 0;
               const tds = parseFloat(fields.tds) || 0;
               if (tds > grossAmount) {
                 setValidationErrors(prev => ({ ...prev, [`${rowId}_tds`]: "TDS cannot exceed Gross Amount" }));
               } else {
                 clearValidationError(`${rowId}_tds`);
               }
             }
           }
           
           // Real-time TDS Claimed vs TDS validation
           if (fieldId === 'tds' || fieldId === 'tdsClaimed' || fieldId === 'tdsDeducted') {
             if (fields.tds && fields.tdsClaimed && fields.tds.trim() !== "" && fields.tdsClaimed.trim() !== "") {
               const tds = parseFloat(fields.tds) || 0;
               const tdsClaimed = parseFloat(fields.tdsClaimed) || 0;
               if (tdsClaimed > tds) {
                 setValidationErrors(prev => ({ ...prev, [`${rowId}_tdsClaimed`]: "TDS Claimed cannot exceed TDS Amount" }));
               } else {
                 clearValidationError(`${rowId}_tdsClaimed`);
               }
             }
           }
           
           // Real-time validation for TDS Claimed - always mandatory when schedule is filled
           if (fieldId === 'tdsClaimed') {
             if (!value || value.trim() === "") {
               setValidationErrors(prev => ({ ...prev, [`${rowId}_tdsClaimed`]: "TDS Claimed is mandatory when schedule is filled" }));
             } else {
               clearValidationError(`${rowId}_tdsClaimed`);
             }
           }
         }
       }
       
       // Real-time validation for TCS tab
       if (activeTab === 'tcs') {
         const row = formData[activeTab].find(row => row.id === rowId);
         if (row) {
           const fields = row.fields;
           
           // Real-time TCS Claimed vs TCS validation
           if (fieldId === 'tcs' || fieldId === 'tcsClaimed' || fieldId === 'amountTaxCollected') {
             if (fields.tcs && fields.tcsClaimed && fields.tcs.trim() !== "" && fields.tcsClaimed.trim() !== "") {
               const tcs = parseFloat(fields.tcs) || 0;
               const tcsClaimed = parseFloat(fields.tcsClaimed) || 0;
               if (tcsClaimed > tcs) {
                 setValidationErrors(prev => ({ ...prev, [`${rowId}_tcsClaimed`]: "TCS Claimed cannot exceed TCS Amount" }));
               } else {
                 clearValidationError(`${rowId}_tcsClaimed`);
               }
             }
           }
           
           // Real-time validation for TCS Claimed - always mandatory when schedule is filled
           if (fieldId === 'tcsClaimed') {
             if (!value || value.trim() === "") {
               setValidationErrors(prev => ({ ...prev, [`${rowId}_tcsClaimed`]: "TCS Claimed is mandatory when schedule is filled" }));
             } else {
               clearValidationError(`${rowId}_tcsClaimed`);
             }
           }
           
           // Real-time validation for conditional fields based on TCS Claim Type
           if (tcsClaimType === "Others") {
             if (fieldId === 'panWhoseTCSClaimed') {
               if (!value || value.trim() === "") {
                 setValidationErrors(prev => ({ ...prev, [`${rowId}_panWhoseTCSClaimed`]: "PAN (whose TCS claimed) is mandatory when TCS Claim Type is 'Others'" }));
               } else {
                 clearValidationError(`${rowId}_panWhoseTCSClaimed`);
               }
             }
             
             if (fieldId === 'name') {
               if (!value || value.trim() === "") {
                 setValidationErrors(prev => ({ ...prev, [`${rowId}_name`]: "Name is mandatory when TCS Claim Type is 'Others'" }));
               } else {
                 clearValidationError(`${rowId}_name`);
               }
             }
           }
         }
       }

    // Clear Tax Paid validation errors when fields become valid
    if (activeTab === 'taxPaid') {
      const row = formData[activeTab].find(row => row.id === rowId);
      if (row) {
        const fields = row.fields;
        const hasAnyValue = Object.values(fields).some(val => val && val.toString().trim() !== "");
        
        if (hasAnyValue) {
          // If all required fields are now filled, clear the validation error for this field
          if (value && value.toString().trim() !== "") {
            clearValidationError(`${rowId}_${fieldId}`);
          }
        }
      }
    }

         // Clear TDS on Salary validation errors when fields become valid
     if (activeTab === 'tdsOnSalary') {
       const row = formData[activeTab].find(row => row.id === rowId);
       if (row) {
         const fields = row.fields;
         const hasAnyValue = Object.values(fields).some(val => val && val.toString().trim() !== "");
         
         if (hasAnyValue) {
           // If all required fields are now filled, clear the validation error for this field
           if (value && value.toString().trim() !== "") {
             clearValidationError(`${rowId}_${fieldId}`);
           }
           
                       // Clear TDS vs Gross Amount validation error if both fields are now valid
            if (fields.grossAmount && fields.tds && fields.grossAmount.trim() !== "" && fields.tds.trim() !== "") {
              const grossAmount = parseFloat(fields.grossAmount) || 0;
              const tds = parseFloat(fields.tds) || 0;
              if (tds <= grossAmount) {
                clearValidationError(`${rowId}_tds`);
              }
            }
          }
        }
      }
      
                           // Clear Other TDS validation errors when fields become valid
        if (activeTab === 'otherTDS') {
          const row = formData[activeTab].find(row => row.id === rowId);
          if (row) {
            const fields = row.fields;
            const hasAnyValue = Object.values(fields).some(val => val && val.toString().trim() !== "");
            
            if (hasAnyValue) {
              // Clear validation errors for common mandatory fields when they become valid
              if (value && value.toString().trim() !== "") {
                // Clear error for the current field
                clearValidationError(`${rowId}_${fieldId}`);
                
                                // Also clear errors for related UI fields if this is an API field
                 if (fieldId === 'tdsSection' || fieldId === 'section') {
                   clearValidationError(`${rowId}_section`);
                 } else if (fieldId === 'deductedYear' || fieldId === 'finYear') {
                   clearValidationError(`${rowId}_finYear`);
                 } else if (fieldId === 'amountForTaxDeduct' || fieldId === 'grossAmount') {
                   clearValidationError(`${rowId}_grossAmount`);
                 } else if (fieldId === 'totalTdsOnAmountPaid' || fieldId === 'tds') {
                   clearValidationError(`${rowId}_tds`);
                 } else if (fieldId === 'claimOutOfTotalTdsOnAmountPaid' || fieldId === 'tdsClaimed') {
                   clearValidationError(`${rowId}_tdsClaimed`);
                 }
              }
              
              // Clear TDS vs Gross Amount validation error if both fields are now valid
              if (fields.grossAmount && fields.tds && fields.grossAmount.trim() !== "" && fields.tds.trim() !== "") {
                const grossAmount = parseFloat(fields.grossAmount) || 0;
                const tds = parseFloat(fields.tds) || 0;
                if (tds <= grossAmount) {
                  clearValidationError(`${rowId}_tds`);
                }
              }
              
              // Clear TDS Claimed vs TDS validation error if both fields are now valid
              if (fields.tds && fields.tdsClaimed && fields.tds.trim() !== "" && fields.tdsClaimed.trim() !== "") {
                const tds = parseFloat(fields.tds) || 0;
                const tdsClaimed = parseFloat(fields.tdsClaimed) || 0;
                if (tdsClaimed <= tds) {
                  clearValidationError(`${rowId}_tdsClaimed`);
                }
              }
            }
          }
        }
        
                 // Clear Other TDS2 validation errors when fields become valid
         if (activeTab === 'otherTDS2') {
           const row = formData[activeTab].find(row => row.id === rowId);
           if (row) {
             const fields = row.fields;
             const hasAnyValue = Object.values(fields).some(val => val && val.toString().trim() !== "");
             
             if (hasAnyValue) {
               // Clear validation errors for common mandatory fields when they become valid
               if (value && value.toString().trim() !== "") {
                 // Clear error for the current field
                 clearValidationError(`${rowId}_${fieldId}`);
                 
                 // Also clear errors for related UI fields if this is an API field
                 if (fieldId === 'tdsSection' || fieldId === 'section') {
                   clearValidationError(`${rowId}_section`);
                 } else if (fieldId === 'deductedYear' || fieldId === 'finYear') {
                   clearValidationError(`${rowId}_finYear`);
                 } else if (fieldId === 'grsReceiptToTaxDeduct' || fieldId === 'grossAmount') {
                   clearValidationError(`${rowId}_grossAmount`);
                 } else if (fieldId === 'tdsDeducted' || fieldId === 'tds') {
                   clearValidationError(`${rowId}_tds`);
                 } else if (fieldId === 'tdsClaimed') {
                   clearValidationError(`${rowId}_tdsClaimed`);
                 }
               }
               
               // Clear TDS vs Gross Amount validation error if both fields are now valid
               if (fields.grossAmount && fields.tds && fields.grossAmount.trim() !== "" && fields.tds.trim() !== "") {
                 const grossAmount = parseFloat(fields.grossAmount) || 0;
                 const tds = parseFloat(fields.tds) || 0;
                 if (tds <= grossAmount) {
                   clearValidationError(`${rowId}_tds`);
                 }
               }
               
               // Clear TDS Claimed vs TDS validation error if both fields are now valid
               if (fields.tds && fields.tdsClaimed && fields.tds.trim() !== "" && fields.tdsClaimed.trim() !== "") {
                 const tds = parseFloat(fields.tds) || 0;
                 const tdsClaimed = parseFloat(fields.tdsClaimed) || 0;
                 if (tdsClaimed <= tds) {
                   clearValidationError(`${rowId}_tdsClaimed`);
                 }
               }
             }
           }
         }
         
         // Clear TCS validation errors when fields become valid
         if (activeTab === 'tcs') {
           const row = formData[activeTab].find(row => row.id === rowId);
           if (row) {
             const fields = row.fields;
             const hasAnyValue = Object.values(fields).some(val => val && val.toString().trim() !== "");
             
             if (hasAnyValue) {
               // Clear validation errors for common mandatory fields when they become valid
               if (value && value.toString().trim() !== "") {
                 // Clear error for the current field
                 clearValidationError(`${rowId}_${fieldId}`);
                 
                 // Also clear errors for related UI fields if this is an API field
                 if (fieldId === 'collectedYear' || fieldId === 'tcsFinYear') {
                   clearValidationError(`${rowId}_tcsFinYear`);
                 } else if (fieldId === 'amountTaxCollected' || fieldId === 'tcs') {
                   clearValidationError(`${rowId}_tcs`);
                 } else if (fieldId === 'amountTcsClaimedThisYear' || fieldId === 'tcsClaimed') {
                   clearValidationError(`${rowId}_tcsClaimed`);
                 }
               }
               
               // Clear TCS Claimed vs TCS validation error if both fields are now valid
               if (fields.tcs && fields.tcsClaimed && fields.tcs.trim() !== "" && fields.tcsClaimed.trim() !== "") {
                 const tcs = parseFloat(fields.tcs) || 0;
                 const tcsClaimed = parseFloat(fields.tcsClaimed) || 0;
                 if (tcsClaimed <= tcs) {
                   clearValidationError(`${rowId}_tcsClaimed`);
                 }
               }
             }
           }
         }
  }

  // Add this function to check if a field has error
  const hasError = (rowId, fieldId) => {
    const row = formData[activeTab].find(row => row.id === rowId);
    if (!row) return false;

    if (fieldId === 'tdsClaimed' || fieldId === 'tcsClaimed') {
      const tdsOrTcsField = fieldId === 'tdsClaimed' ? 'tds' : 'tcs';
      const claimedAmount = parseFloat(row.fields[fieldId]) || 0;
      const originalAmount = parseFloat(row.fields[tdsOrTcsField]) || 0;

      return claimedAmount > originalAmount;
    }
    return false;
  };

  // Add function to get validation error for a field
  const getFieldError = (rowId, fieldId) => {
    return validationErrors[`${rowId}_${fieldId}`] || null;
  };

  // Handle field click from error sidebar
  const handleFieldClick = (fieldKey) => {
    const [rowId, fieldId] = fieldKey.split('_');
    if (rowId && fieldId) {
      // Find the input element and focus it
      const inputElement = document.querySelector(`input[data-row-id="${rowId}"][data-field-id="${fieldId}"]`);
      if (inputElement) {
        inputElement.focus();
        inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  // Handle closing the error sidebar
  const handleCloseErrorSidebar = () => {
    // This function is no longer needed as the sidebar is always visible when enabled
    // But keeping it for compatibility
  }

  // Validation function for Tax Paid tab - if any field is filled, all fields are mandatory
  const validateTaxPaidFields = () => {
    const errors = {};
    
    formData.taxPaid.forEach((row, index) => {
      const fields = row.fields;
      const hasAnyValue = Object.values(fields).some(val => val && val.toString().trim() !== "");
      
      if (hasAnyValue) {
        // Check if all required fields are filled
        if (!fields.dateOfDeposit || fields.dateOfDeposit.trim() === "") {
          errors[`${row.id}_dateOfDeposit`] = "Date of Deposit is mandatory when schedule is filled";
        }
        if (!fields.bsrCode || fields.bsrCode.trim() === "") {
          errors[`${row.id}_bsrCode`] = "BSR Code is mandatory when schedule is filled";
        }
        if (!fields.challanNo || fields.challanNo.trim() === "") {
          errors[`${row.id}_challanNo`] = "Challan No. is mandatory when schedule is filled";
        }
        
        // Additional validation: if amount > 0, challan number must be provided
        const amountValue = Number.parseFloat(fields.amount) || 0;
        if (amountValue > 0 && (!fields.challanNo || fields.challanNo.trim() === "")) {
          errors[`${row.id}_challanNo`] = "Serial Number of Challan is required when amount is greater than 0";
        }
        if (!fields.amount || fields.amount.trim() === "") {
          errors[`${row.id}_amount`] = "Amount is mandatory when schedule is filled";
        }
      }
    });
    
    setValidationErrors(prev => ({ ...prev, ...errors }));
    return Object.keys(errors).length === 0;
  };

  // Validation function for TDS on Salary tab - if any field is filled, all fields are mandatory
  const validateTdsOnSalaryFields = () => {
    const errors = {};
    
    formData.tdsOnSalary.forEach((row, index) => {
      const fields = row.fields;
      const hasAnyValue = Object.values(fields).some(val => val && val.toString().trim() !== "");
      
      if (hasAnyValue) {
        // Check if all required fields are filled
        if (!fields.tan || fields.tan.trim() === "") {
          errors[`${row.id}_tan`] = "TAN is mandatory when schedule is filled";
        } else if (fields.tan && fields.tan.trim() !== "") {
          // Validate TAN format if TAN is provided
          const tanPattern = centralizedPatterns.TAN;
          if (!tanPattern.test(fields.tan.trim())) {
            errors[`${row.id}_tan`] = "Invalid TAN format. Expected format: DELA12345F";
          }
        }
        if (!fields.nameOfEmployer || fields.nameOfEmployer.trim() === "") {
          errors[`${row.id}_nameOfEmployer`] = "Name of Employer is mandatory when schedule is filled";
        }
        if (!fields.grossAmount || fields.grossAmount.trim() === "") {
          errors[`${row.id}_grossAmount`] = "Gross Amount is mandatory when schedule is filled";
        }
        if (!fields.tds || fields.tds.trim() === "") {
          errors[`${row.id}_tds`] = "TDS is mandatory when schedule is filled";
        }
        
        // Validate that TDS should not exceed Gross Amount
        if (fields.grossAmount && fields.tds && fields.grossAmount.trim() !== "" && fields.tds.trim() !== "") {
          const grossAmount = parseFloat(fields.grossAmount) || 0;
          const tds = parseFloat(fields.tds) || 0;
          if (tds > grossAmount) {
            errors[`${row.id}_tds`] = "TDS cannot exceed Gross Amount";
          }
        }
      }
    });
    
    setValidationErrors(prev => ({ ...prev, ...errors }));
    return Object.keys(errors).length === 0;
  };

     // Validation function for Other TDS tab - based on TDS Claim Type selection
   const validateOtherTdsFields = () => {
     const errors = {};
     
     formData.otherTDS.forEach((row, index) => {
       const fields = row.fields;
       const hasAnyValue = Object.values(fields).some(val => val && val.toString().trim() !== "");
       
       if (hasAnyValue) {
         // Common mandatory fields for both TDS Claim Types
         if (!fields.tanOfDeductor || fields.tanOfDeductor.trim() === "") {
           errors[`${row.id}_tanOfDeductor`] = "TAN of Deductor is mandatory when schedule is filled";
         } else if (fields.tanOfDeductor && fields.tanOfDeductor.trim() !== "") {
           // Validate TAN format if TAN is provided
           const tanPattern = centralizedPatterns.TAN;
           if (!tanPattern.test(fields.tanOfDeductor.trim())) {
             errors[`${row.id}_tanOfDeductor`] = "Invalid TAN format. Expected format: DELA12345F";
           }
         }
         
         if (!fields.name || fields.name.trim() === "") {
           errors[`${row.id}_name`] = "Name is mandatory when schedule is filled";
         }
         
                  // Check Section - validate against both UI field and API field
         const sectionValue = fields.section || fields.tdsSection || "";
         if (!sectionValue || sectionValue.trim() === "") {
           errors[`${row.id}_section`] = "Section is mandatory when schedule is filled";
         }
         
         // Check Financial Year - validate against both UI field and API field
         const finYearValue = fields.finYear || fields.deductedYear || "";
         if (!finYearValue || finYearValue.trim() === "") {
           errors[`${row.id}_finYear`] = "Financial Year is mandatory when schedule is filled";
         }
         
        if (!fields.grossAmount || fields.grossAmount.trim() === "") {
           errors[`${row.id}_grossAmount`] = "Gross Amount is mandatory when schedule is filled";
         }
         
         if (!fields.tds || fields.tds.trim() === "") {
           errors[`${row.id}_tds`] = "TDS is mandatory when schedule is filled";
         }
         
                  // Conditional mandatory fields based on TDS Claim Type
         if (tdsClaimType === "Others") {
           if (!fields.panWhoseTDSClaimed || fields.panWhoseTDSClaimed.trim() === "") {
             errors[`${row.id}_panWhoseTDSClaimed`] = "PAN (whose TDS claimed) is mandatory when TDS Claim Type is 'Others'";
           } else if (fields.panWhoseTDSClaimed && fields.panWhoseTDSClaimed.trim() !== "") {
             // Validate PAN format if PAN is provided
             const panPattern = centralizedPatterns.PAN;
             if (!panPattern.test(fields.panWhoseTDSClaimed.trim())) {
               errors[`${row.id}_panWhoseTDSClaimed`] = "Invalid PAN format. Expected format: ABCDE1234F";
             }
           }
         }
         
         // TDS Claimed is always mandatory when schedule is filled (regardless of TDS Claim Type)
         const tdsClaimedValue = fields.claimOutOfTotalTdsOnAmountPaid || fields.tdsClaimed || "";
         if (!tdsClaimedValue || tdsClaimedValue.trim() === "") {
           errors[`${row.id}_tdsClaimed`] = "TDS Claimed is mandatory when schedule is filled";
         }
         
        // Amount validation rules
         if (fields.grossAmount && fields.tds && fields.grossAmount.trim() !== "" && fields.tds.trim() !== "") {
           const grossAmount = parseFloat(fields.grossAmount) || 0;
           const tds = parseFloat(fields.tds) || 0;
           
           if (tds > grossAmount) {
             errors[`${row.id}_tds`] = "TDS cannot exceed Gross Amount";
           }
         }
         
         if (fields.tds && fields.tdsClaimed && fields.tds.trim() !== "" && fields.tdsClaimed.trim() !== "") {
           const tds = parseFloat(fields.tds) || 0;
           const tdsClaimed = parseFloat(fields.tdsClaimed) || 0;
           
           if (tdsClaimed > tds) {
             errors[`${row.id}_tdsClaimed`] = "TDS Claimed cannot exceed TDS Amount";
           }
         }
       }
     });
     
     setValidationErrors(prev => ({ ...prev, ...errors }));
     return Object.keys(errors).length === 0;
   };

   // Validation function for Other TDS2 tab - if any field is filled, all fields are mandatory
   const validateOtherTds2Fields = () => {
     const errors = {};
     
     formData.otherTDS2.forEach((row, index) => {
       const fields = row.fields;
       const hasAnyValue = Object.values(fields).some(val => val && val.toString().trim() !== "");
       
       if (hasAnyValue) {
         // Check if all required fields are filled
         if (!fields.name || fields.name.trim() === "") {
           errors[`${row.id}_name`] = "Name is mandatory when schedule is filled";
         }
         
         if (!fields.panOfBuyerTenant || fields.panOfBuyerTenant.trim() === "") {
           errors[`${row.id}_panOfBuyerTenant`] = "PAN of Buyer/Tenant is mandatory when schedule is filled";
         } else if (fields.panOfBuyerTenant && fields.panOfBuyerTenant.trim() !== "") {
           // Validate PAN format if PAN is provided
           const panPattern = centralizedPatterns.PAN;
           if (!panPattern.test(fields.panOfBuyerTenant.trim())) {
             errors[`${row.id}_panOfBuyerTenant`] = "Invalid PAN format. Expected format: ABCDE1234F";
           }
         }
         
         // Check Section - validate against both UI field and API field
         const sectionValue = fields.section || fields.tdsSection || "";
         if (!sectionValue || sectionValue.trim() === "") {
           errors[`${row.id}_section`] = "Section is mandatory when schedule is filled";
         }
         
         // Check Financial Year - validate against both UI field and API field (use finYear2 for otherTDS2 tab)
         const finYearValue = fields.finYear2 || fields.deductedYear || "";
         if (!finYearValue || finYearValue.trim() === "") {
           errors[`${row.id}_finYear2`] = "Financial Year is mandatory when schedule is filled";
         }
         
         if (!fields.grossAmount || fields.grossAmount.trim() === "") {
           errors[`${row.id}_grossAmount`] = "Gross Amount is mandatory when schedule is filled";
         }
         
         if (!fields.tds || fields.tds.trim() === "") {
           errors[`${row.id}_tds`] = "TDS is mandatory when schedule is filled";
         }
         
         if (!fields.tdsClaimed || fields.tdsClaimed.trim() === "") {
           errors[`${row.id}_tdsClaimed`] = "TDS Claimed is mandatory when schedule is filled";
         }
         
         // Amount validation rules
         if (fields.grossAmount && fields.tds && fields.grossAmount.trim() !== "" && fields.tds.trim() !== "") {
           const grossAmount = parseFloat(fields.grossAmount) || 0;
           const tds = parseFloat(fields.tds) || 0;
           
           if (tds > grossAmount) {
             errors[`${row.id}_tds`] = "TDS cannot exceed Gross Amount";
           }
         }
         
         if (fields.tds && fields.tdsClaimed && fields.tds.trim() !== "" && fields.tdsClaimed.trim() !== "") {
           const tds = parseFloat(fields.tds) || 0;
           const tdsClaimed = parseFloat(fields.tdsClaimed) || 0;
           
           if (tdsClaimed > tds) {
             errors[`${row.id}_tdsClaimed`] = "TDS Claimed cannot exceed TDS Amount";
           }
         }
       }
     });
     
     setValidationErrors(prev => ({ ...prev, ...errors }));
     return Object.keys(errors).length === 0;
   };

       // Validation function for TCS tab - if any field is filled, all fields are mandatory based on TCS Claim Type
    const validateTcsFields = () => {
      const errors = {};
      
      formData.tcs.forEach((row, index) => {
        const fields = row.fields;
        const hasAnyValue = Object.values(fields).some(val => val && val.toString().trim() !== "");
        
        if (hasAnyValue) {
          // Common mandatory fields for both TCS Claim Types
          if (!fields.tanOfCollector || fields.tanOfCollector.trim() === "") {
            errors[`${row.id}_tanOfCollector`] = "TAN of Collector is mandatory when schedule is filled";
          } else if (fields.tanOfCollector && fields.tanOfCollector.trim() !== "") {
            // Validate TAN format if TAN is provided
            const tanPattern = centralizedPatterns.TAN;
            if (!tanPattern.test(fields.tanOfCollector.trim())) {
              errors[`${row.id}_tanOfCollector`] = "Invalid TAN format. Expected format: DELA12345F";
            }
          }
          
          // Check Financial Year - validate against both UI field and API field
          const finYearValue = fields.tcsFinYear || fields.collectedYear || "";
          if (!finYearValue || finYearValue.trim() === "") {
            errors[`${row.id}_tcsFinYear`] = "Financial Year is mandatory when schedule is filled";
          }
          
          if (!fields.tcs || fields.tcs.trim() === "") {
            errors[`${row.id}_tcs`] = "TCS is mandatory when schedule is filled";
          }
          
          if (!fields.tcsClaimed || fields.tcsClaimed.trim() === "") {
            errors[`${row.id}_tcsClaimed`] = "TCS Claimed is mandatory when schedule is filled";
          }
          
          // Conditional mandatory fields based on TCS Claim Type
          if (tcsClaimType === "Others") {
            if (!fields.panWhoseTCSClaimed || fields.panWhoseTCSClaimed.trim() === "") {
              errors[`${row.id}_panWhoseTCSClaimed`] = "PAN (whose TCS claimed) is mandatory when TCS Claim Type is 'Others'";
            } else if (fields.panWhoseTCSClaimed && fields.panWhoseTCSClaimed.trim() !== "") {
              // Validate PAN format if PAN is provided
              const panPattern = centralizedPatterns.PAN;
              if (!panPattern.test(fields.panWhoseTCSClaimed.trim())) {
                errors[`${row.id}_panWhoseTCSClaimed`] = "Invalid PAN format. Expected format: ABCDE1234F";
              }
            }
            
            if (!fields.name || fields.name.trim() === "") {
              errors[`${row.id}_name`] = "Name is mandatory when TCS Claim Type is 'Others'";
            }
          }
          
          // Amount validation rules
          if (fields.tcs && fields.tcsClaimed && fields.tcs.trim() !== "" && fields.tcsClaimed.trim() !== "") {
            const tcs = parseFloat(fields.tcs) || 0;
            const tcsClaimed = parseFloat(fields.tcsClaimed) || 0;
            
            if (tcsClaimed > tcs) {
              errors[`${row.id}_tcsClaimed`] = "TCS Claimed cannot exceed TCS Amount";
            }
          }
        }
      });
      
      setValidationErrors(prev => ({ ...prev, ...errors }));
      return Object.keys(errors).length === 0;
    };

  // Clear validation errors for a specific field
  const clearValidationError = (errorKey) => {
    setValidationErrors(prev => {
      const next = { ...prev };
      delete next[errorKey];
      return next;
    });
  };

  // Handle Tax Paid PDF data extraction
  const handleTaxPaidPDFDataExtracted = (extractedData) => {
    console.log('Tax Paid PDF data extracted:', extractedData);
    
    // Add the extracted data to the existing tax paid data
    setFormData(prev => ({
      ...prev,
      taxPaid: [...prev.taxPaid.filter(row => 
        Object.values(row.fields || {}).some(value => value && value.toString().trim() !== "")
      ), ...extractedData]
    }));
    
    toast.success(`Successfully imported ${extractedData.length} tax payment entries from PDF`);
  };

  // Handle Other TDS JSON data extraction - REMOVED
  // const handleOtherTDSJSONDataExtracted = (extractedData) => {
  //   console.log('Other TDS JSON data extracted:', extractedData);
  //   
  //   // Add the extracted data to the existing other TDS data
  //   setFormData(prev => ({
  //     ...prev,
  //     otherTDS: [...prev.otherTDS.filter(row => 
  //       Object.values(row.fields || {}).some(value => value && value.toString().trim() !== "")
  //     ), ...extractedData]
  //   }));
  //   
  //   // Close the JSON uploader after successful import
  //   setShowJSONUploader(false);
  //   
  //   toast.success(`Successfully imported ${extractedData.length} Other TDS entries from JSON`);
  // };


  // Handle 26AS PDF data extraction - categorized data for all tabs
  const handlePDFDataExtracted = (categorizedData, targetTab) => {
    if (targetTab === "all") {
      // Process all categories from 26AS
      const newFormData = { ...formData }
      let totalImported = 0

      // Process Tax Paid
      if (categorizedData.taxPaid?.length > 0) {
        const mappedTaxPaid = categorizedData.taxPaid.map(entry => ({
          id: (Date.now().toString(36) + Math.random().toString(36).substring(2)),
          apiId: null,
          fields: {
            dateOfDeposit: entry.dateOfDeposit || "",
            bsrCode: entry.bsrCode || "",
            challanNo: entry.challanNo || "",
            amount: entry.amount?.toString() || "",
          }
        }))
        newFormData.taxPaid = [...newFormData.taxPaid.filter(row => 
          Object.values(row.fields || {}).some(value => value && value.toString().trim() !== "")
        ), ...mappedTaxPaid]
        totalImported += mappedTaxPaid.length
      }

      // Process TDS on Salary
      if (categorizedData.tdsOnSalary?.length > 0) {
        const mappedTdsOnSalary = categorizedData.tdsOnSalary.map(entry => ({
          id: (Date.now().toString(36) + Math.random().toString(36).substring(2)),
          apiId: null,
          fields: {
            tan: entry.tan || "",
            nameOfEmployer: entry.deductorName || "",
            grossAmount: entry.grossAmount?.toString() || "",
            tds: entry.tds?.toString() || "",
          }
        }))
        newFormData.tdsOnSalary = [...newFormData.tdsOnSalary.filter(row => 
          Object.values(row.fields || {}).some(value => value && value.toString().trim() !== "")
        ), ...mappedTdsOnSalary]
        totalImported += mappedTdsOnSalary.length
      }

      // Process Other TDS (with TAN)
      if (categorizedData.otherTDS?.length > 0) {
        const mappedOtherTds = categorizedData.otherTDS.map(entry => ({
          id: (Date.now().toString(36) + Math.random().toString(36).substring(2)),
          apiId: null,
          selfTDS: false,
          selfClaimed: false,
          fields: {
            name: entry.deductorName || "",
            panOfBuyerTenant: entry.tan || "",
            section: entry.section || "",
            finYear: "2024-25",
            grossAmount: entry.grossAmount?.toString() || "",
            tds: entry.tds?.toString() || "",
            tdsClaimed: entry.tdsClaimed?.toString() || "",
          }
        }))
        newFormData.otherTDS = [...newFormData.otherTDS.filter(row => 
          Object.values(row.fields || {}).some(value => value && value.toString().trim() !== "")
        ), ...mappedOtherTds]
        totalImported += mappedOtherTds.length
      }

      // Process Other TDS2 (with PAN)
      if (categorizedData.otherTDS2?.length > 0) {
        const mappedOtherTds2 = categorizedData.otherTDS2.map(entry => ({
          id: (Date.now().toString(36) + Math.random().toString(36).substring(2)),
          apiId: null,
          selfTDS: false,
          selfClaimed: false,
          fields: {
            name: entry.deductorName || "",
            panOfBuyerTenant: entry.pan || "",
            section: entry.section || "",
            finYear: "2024-25",
            grossAmount: entry.grossAmount?.toString() || "",
            tds: entry.tds?.toString() || "",
            tdsClaimed: entry.tdsClaimed?.toString() || "",
          }
        }))
        newFormData.otherTDS2 = [...newFormData.otherTDS2.filter(row => 
          Object.values(row.fields || {}).some(value => value && value.toString().trim() !== "")
        ), ...mappedOtherTds2]
        totalImported += mappedOtherTds2.length
      }

      // Process TCS
      if (categorizedData.tcs?.length > 0) {
        const mappedTcs = categorizedData.tcs.map(entry => ({
          id: (Date.now().toString(36) + Math.random().toString(36).substring(2)),
          apiId: null,
          selfTDS: false,
          selfClaimed: false,
          fields: {
            panWhoseTCSClaimed: entry.pan || "",
            name: entry.deductorName || "",
            tanOfCollector: entry.tan || "",
            finYear: "2024-25",
            tcs: entry.tcs?.toString() || "",
            tcsClaimed: entry.tcsClaimed?.toString() || "",
          }
        }))
        newFormData.tcs = [...newFormData.tcs.filter(row => 
          Object.values(row.fields || {}).some(value => value && value.toString().trim() !== "")
        ), ...mappedTcs]
        totalImported += mappedTcs.length
      }

      setFormData(newFormData)
      toast.success(`Successfully imported ${totalImported} total entries from 26AS PDF across all tabs`)
    }
  }

  const handleRadioChange = (rowId, fieldId, value) => {
    setFormData((prev) => {
      const newData = { ...prev }
      const rows = [...newData[activeTab]]
      const rowIndex = rows.findIndex((row) => row.id === rowId)
      if (rowIndex !== -1) {
        rows[rowIndex] = {
          ...rows[rowIndex],
          [fieldId]: value,
        }
      }
      newData[activeTab] = rows
      return newData
    })
  }

  const handleCheckboxChange = (rowId, fieldId) => {
    setFormData((prev) => {
      const newData = { ...prev }
      const rows = [...newData[activeTab]]
      const rowIndex = rows.findIndex((row) => row.id === rowId)
      if (rowIndex !== -1) {
        rows[rowIndex] = {
          ...rows[rowIndex],
          [fieldId]: !rows[rowIndex][fieldId],
        }
      }
      newData[activeTab] = rows
      return newData
    })
  }

  const addRow = () => {
    setFormData((prev) => {
      const newData = { ...prev }
      newData[activeTab] = [...newData[activeTab], generateEmptyRow(activeTab)]
      return newData
    })
  }

  const removeRow = async (rowId, deleteData) => {
    console.log("delete data", deleteData, activeTab)
    if (deleteData?.apiId && activeTab == "taxPaid") {
      try {
        await axios.delete(
          `${config.apiUrl}/taxpaid/taxpayment/${deleteData.apiId}`
        );
        toast.success("Record deleted successfully");
      } catch (error) {
        toast.error("Failed to delete record");
        console.error("Error deleting exempt income:", error);
        return; // Don't remove from UI if delete failed
      }
    }
    if (deleteData?.apiId && activeTab == "tdsOnSalary") {
      try {
        await axios.delete(
          `${config.apiUrl}/taxpaid/tdsonsalary/${deleteData.apiId}`
        );
        toast.success("Record deleted successfully");
      } catch (error) {
        toast.error("Failed to delete record");
        console.error("Error deleting exempt income:", error);
        return; // Don't remove from UI if delete failed
      }
    }
    if (deleteData?.apiId && activeTab == "otherTDS") {
      try {
        await axios.delete(
          `${config.apiUrl}/taxpaid/tds2/${deleteData.apiId}`
        );
        toast.success("Record deleted successfully");
      } catch (error) {
        toast.error("Failed to delete record");
        console.error("Error deleting exempt income:", error);
        return; // Don't remove from UI if delete failed
      }
    }
    if (deleteData?.apiId && activeTab == "otherTDS2") {
      try {
        await axios.delete(
          `${config.apiUrl}/taxpaid/tds3/${deleteData.apiId}`
        );
        toast.success("Record deleted successfully");
      } catch (error) {
        toast.error("Failed to delete record");
        console.error("Error deleting exempt income:", error);
        return; // Don't remove from UI if delete failed
      }
    }
    if (deleteData?.apiId && activeTab == "tcs") {
      try {
        await axios.delete(
          `${config.apiUrl}/taxpaid/tcs/${deleteData.apiId}`
        );
        toast.success("Record deleted successfully");
      } catch (error) {
        toast.error("Failed to delete record");
        console.error("Error deleting exempt income:", error);
        return; // Don't remove from UI if delete failed
      }
    }

    setFormData((prev) => {
      const newData = { ...prev }
      const updatedRows = newData[activeTab].filter((row) => row.id !== rowId)
      // Ensure there's always at least one row
      newData[activeTab] = updatedRows.length > 0 ? updatedRows : [generateEmptyRow(activeTab)]
      return newData
    })
  }

  const calculateTotal = (fieldId, tabId = null) => {
    // Use specified tab or activeTab if none provided
    const rows = tabId ? formData[tabId] : formData[activeTab];

    // Special case for taxPaid tab which sums the amount field
    if ((tabId || activeTab) === "taxPaid" && fieldId === "amount") {
      const total = rows.reduce((sum, row) => {
        const value = Number.parseFloat(row.fields.amount || "0");
        return sum + (isNaN(value) ? 0 : value);
      }, 0);
      return total.toFixed(2);
    }

    // For other tabs, calculate based on the fieldId
    const total = rows.reduce((sum, row) => {
      let value = 0;

      // For tds/tcs claimed in own hand, only include if selfClaimed is true
      if (fieldId === "tdsClaimed" || fieldId === "tcsClaimed") {
        if (row.selfClaimed) {
          value = Number.parseFloat(row.fields[fieldId] || "0");
        }
      }
      // For claimed by others, only include if selfClaimed is false
      else if (fieldId === "claimedByOthers") {
        if (!row.selfClaimed) {
          const claimField = (tabId || activeTab) === "tcs" ? "tcsClaimed" : "tdsClaimed";
          value = Number.parseFloat(row.fields[claimField] || "0");
        }
      }
      // For regular totals (tds/tcs)
      else {
        value = Number.parseFloat(row.fields[fieldId] || "0");
      }

      return sum + (isNaN(value) ? 0 : value);
    }, 0);

    return total.toFixed(2);
  };

  const calculateTotalTaxValues = () => {
    console.log('Starting total tax values calculation...');

    // 1. Calculate Tax Paid Total
    const taxPaidTotal = Number.parseFloat(calculateTotal("amount", "taxPaid")) || 0;

    // 2. Calculate TDS on Salary Total
    const tdsOnSalaryTotal = Number.parseFloat(calculateTotal("tds", "tdsOnSalary")) || 0;

    // 3. Calculate Other TDS Claimed (first tab)
    const otherTdsClaimed = Number.parseFloat(calculateTotal("claimedByOthers", "otherTDS")) || 0;

    // 4. Calculate Other TDS Claimed (second tab)
    const otherTds2Claimed = Number.parseFloat(calculateTotal("claimedByOthers", "otherTDS2")) || 0;

    // 5. Calculate TCS Claimed
    const tcsClaimed = Number.parseFloat(calculateTotal("claimedByOthers", "tcs")) || 0;

    // 6. Calculate Grand Total
    const total = taxPaidTotal + tdsOnSalaryTotal + otherTdsClaimed + otherTds2Claimed + tcsClaimed;

    const formattedTotal = total.toFixed(2);

    return formattedTotal;
  };

  const handleSubmit = async () => {
    let hasValidationError = false;
    
    // Validate Tax Paid tab fields - if any field is filled, all fields are mandatory
    if (activeTab === 'taxPaid') {
      if (!validateTaxPaidFields()) {
        hasValidationError = true;
        toast.error("Please fill all mandatory fields in Tax Paid schedule");
      }
    }
    
         // Validate TDS on Salary tab fields - if any field is filled, all fields are mandatory
     if (activeTab === 'tdsOnSalary') {
       if (!validateTdsOnSalaryFields()) {
         hasValidationError = true;
         toast.error("Please fill all mandatory fields in TDS on Salary schedule");
       }
     }
     
           // Validate Other TDS tab fields - based on TDS Claim Type selection
      if (activeTab === 'otherTDS') {
        if (!validateOtherTdsFields()) {
          hasValidationError = true;
          toast.error("Please fill all mandatory fields in Other TDS schedule");
        }
      }
      
      // Validate Other TDS2 tab fields - if any field is filled, all fields are mandatory
      if (activeTab === 'otherTDS2') {
        if (!validateOtherTds2Fields()) {
          hasValidationError = true;
          toast.error("Please fill all mandatory fields in Other TDS2 schedule");
        }
      }
      
      // Validate TCS tab fields - if any field is filled, all fields are mandatory
      if (activeTab === 'tcs') {
        if (!validateTcsFields()) {
          hasValidationError = true;
          toast.error("Please fill all mandatory fields in TCS schedule");
        }
      }
    
    formData[activeTab].forEach(row => {
      if (activeTab === 'otherTDS' || activeTab === 'otherTDS2') {
        const tds = parseFloat(row.fields.tds) || 0;
        const tdsClaimed = parseFloat(row.fields.tdsClaimed) || 0;
        if (tdsClaimed > tds) {
          hasValidationError = true;
          toast.error(`Row ${row.id}: TDS Claimed cannot be greater than TDS Amount`);
        }
      } else if (activeTab === 'tcs') {
        const tcs = parseFloat(row.fields.tcs) || 0;
        const tcsClaimed = parseFloat(row.fields.tcsClaimed) || 0;
        if (tcsClaimed > tcs) {
          hasValidationError = true;
          toast.error(`Row ${row.id}: TCS Claimed cannot be greater than TCS Amount`);
        }
      }
    });

    if (hasValidationError) {
      return; // Don't proceed with submission
    }
    setIsSubmitting(true)
    try {
      const panNo = localStorage.getItem("PAN_NO")
      const year = localStorage.getItem("YEAR")
      // First validate all claimed amounts

      if (!panNo || !year) {
        toast.error("PAN number or Year not found in local storage.")
        setIsSubmitting(false)
        return
      }

      const payload = {
        taxPaid: {
          // Initialize top-level fields, assuming they are not directly editable in the current UI
          taxPaId: taxPaidId, // Example default, adjust if needed
          advanceTax: 0,
          selfAssessmentTax: 0,
          tds: Number.parseFloat(calculateTotal("tds")) || 0, // Overall TDS total
          tcs: Number.parseFloat(calculateTotal("tcs")) || 0, // Overall TCS total
          taxPayment: formData.taxPaid
            .filter((row) => Object.values(row.fields).some((val) => val !== "")) // Filter out empty rows
            .map((row) => {
              const taxPaymentData = {
                taxPaymentId: row.apiId || null, // Use API ID if exists, else null for new entries
                bsrCode: row.fields.bsrCode,
                dateOfDeposit: row.fields.dateOfDeposit,
                serialNumberOfChallan: row.fields.challanNo,
                amount: Number.parseFloat(row.fields.amount) || 0,
              };
              
              // Debug logging for tax payment data
              console.log('Tax Payment Data being sent:', taxPaymentData);
              console.log('Challan No field value:', row.fields.challanNo);
              console.log('Serial Number of Challan mapped value:', taxPaymentData.serialNumberOfChallan);
              
              return taxPaymentData;
            }),
          tdsOnSalaryDto: formData.tdsOnSalary
            .filter((row) => Object.values(row.fields).some((val) => val !== ""))
            .map((row) => ({
              tdsOnSalaryId: row.apiId || null,
              name: row.fields.nameOfEmployer,
              tan: row.fields.tan,
              incomeChargeableToSalary: Number.parseFloat(row.fields.grossAmount) || 0,
              TotalTdsOnSalary: Number.parseFloat(row.fields.tds) || 0,
            })),
          tdsOnOtherThanSalaryTwos: formData.otherTDS
            .filter((row) => Object.values(row.fields).some((val) => val !== ""))
            .map((row) => ({
              tdsOnOtherThanSalaryTwoId: row.apiId || null,
              name: row.fields.name, // Use lowercase 'name' field as per API
              tan: row.fields.tanOfDeductor,
              tdsSection: row.fields.section, // UI field maps to API tdsSection
              description: "", // Add required description field
              deductedYear: row.fields.finYear, // UI field maps to API deductedYear
              amountForTaxDeduct: Number.parseFloat(row.fields.grossAmount) || 0,
              totalTdsOnAmountPaid: Number.parseFloat(row.fields.tds) || 0,
              claimOutOfTotalTdsOnAmountPaid: Number.parseFloat(row.fields.tdsClaimed) || 0,
            })),
          tdsOnOtherThanSalaryThrees: formData.otherTDS2
            .filter((row) => Object.values(row.fields).some((val) => val !== ""))
            .map((row) => ({
              tdsOnOtherThanSalaryThreeId: row.apiId || null,
              nameOfTenant: row.fields.name || "", // Use name field
              panOfTenant: row.fields.panOfBuyerTenant,
              aadhaarOfTenant: "", // Not directly mapped from UI, default empty
              tdsSection: row.fields.section, // UI field maps to API tdsSection  
              description: "", // Add required description field
              deductedYear: row.fields.finYear2, // UI field for otherTDS2 tab
              grsReceiptToTaxDeduct: Number.parseFloat(row.fields.grossAmount) || 0,
              tdsDeducted: Number.parseFloat(row.fields.tds) || 0,
              tdsClaimed: Number.parseFloat(row.fields.tdsClaimed) || 0,
            })),
          tcss: formData.tcs
            .filter((row) => Object.values(row.fields).some((val) => val !== ""))
            .map((row) => ({
              tcsId: row.apiId || null, // Use correct field name 'tcsId'
              name: row.fields.name || row.fields.panWhoseTCSClaimed, // Use name field
              tan: row.fields.tanOfCollector,
              amountTaxCollected: Number.parseFloat(row.fields.tcs) || 0, // API expects number
              collectedYear: row.fields.tcsFinYear,
              totalTcs: Number.parseFloat(row.fields.tcs) || 0, // API expects number
              amountTcsClaimedThisYear: Number.parseFloat(row.fields.tcsClaimed) || 0, // API expects number
            })),
          // Include other top-level objects with default/empty values if not managed by UI
          taxComputation: {
            taxComputationId: null, // Assuming new entry, adjust if needed
            incomeFromSalary: 0,
            incomeFromHP: 0,
            incomeFromCapitalGain: 0,
            incomeFromOtherSources: 0,
            grossTotalIncome: 0,
            totalDeductionVia: 0,
            totalTaxableIncome: 0,
            totalTaxPayable: 0,
            rebate87A: 0,
            taxPayable: 0,
            educationCess: 0,
            grossTaxLiability: 0,
            relief89A: 0,
            netTaxLiability: 0,
            interestPayable234A: 0,
            lateFilingFee234F: 0,
            interestPayable234B: 0,
            interestPayable234C: 0,
            totalTaxPlusInterest: 0,
            totalTaxPaid: 0,
            netTaxPayable: 0,
            refund: 0,
          },
          //   deductionUndChapVIAType: {
          //     deductionUndChapViaTypeId: "",
          //     section80C: 0,
          //     section80CCC: 0,
          //     section80CCDEmployeeOrSE: 0,
          //     section80CCD1B: 0,
          //     section80CCDEmployer: 0,
          //     section80D: 0,
          //     section80DD: 0,
          //     section80DDB: 0,
          //     section80E: 0,
          //     section80EE: 0,
          //     section80EEA: 0,
          //     section80EEB: 0,
          //     section80G: 0,
          //     section80GG: 0,
          //     section80GGA: 0,
          //     section80GGC: 0,
          //     section80U: 0,
          //     section80TTA: 0,
          //     section80TTB: 0,
          //     anyOthSec80CCH: 0,
          //     totalChapVIADeductions: 0,
          //   },
          //   deductionUsrUndChapVIAType: {
          //     deductionUserUndChapViaTypeId: "",
          //     section80C: 0,
          //     section80CCC: 0,
          //     section80CCDEmployeeOrSE: 0,
          //     section80CCD1B: 0,
          //     section80CCDEmployer: 0,
          //     pranNum: "",
          //     section80D: 0,
          //     section80DD: 0,
          //     section80DDBUsrType: 0,
          //     nameOfSpecDisease80DDB: "",
          //     section80DDB: 0,
          //     section80E: 0,
          //     section80EE: 0,
          //     section80EEA: 0,
          //     section80EEB: 0,
          //     section80G: 0,
          //     section80GG: 0,
          //     form10BAAckNum: 0,
          //     section80GGA: 0,
          //     section80GGC: 0,
          //     section80U: 0,
          //     section80TTA: 0,
          //     section80TTB: 0,
          //     anyOthSec80CCH: 0,
          //     totalChapVIADeductions: 0,
          //   },
        },
      }

      // Debug logging for the complete payload
      console.log('Complete payload being sent to API:', JSON.stringify(payload, null, 2));
      console.log('Tax Payment array:', payload.taxPayment);
      console.log('TCS payload being sent:', payload.tcss);
      
      await axios.patch(`${config.apiUrl}/taxpaid/${panNo}/${year}`, payload)
      // Calculate and store the total tax value
      const totalTaxValue = calculateTotalTaxValues();
      localStorage.setItem("total_tds_tcs_value", totalTaxValue);
      const saved = markPageAsSaved('tds-tcs')
      if (saved) {
        // Clear only TDS prefill data since this page is now saved
        localStorage.removeItem("prefill_tax_paid");
        localStorage.removeItem("prefill_other_tds");
        localStorage.removeItem("prefill_tcs");
        toast.success("TDS data saved successfully!")
      } else {
        toast.error("Please save your profile first before saving TDS details!")
      }
      //  Calculate TDS 
      const totalTds =
        (Number.parseFloat(calculateTotal("tds", "tdsOnSalary")) || 0) +
        (Number.parseFloat(calculateTotal("claimedByOthers", "otherTDS")) || 0) +
        (Number.parseFloat(calculateTotal("claimedByOthers", "otherTDS2")) || 0);

      //  Calculate TCS Claimed
      const totalTcs = Number.parseFloat(calculateTotal("claimedByOthers", "tcs")) || 0;
      await taxComputation({ totalTds, totalTcs })
      fetchDeductionData() // Re-fetch to get updated IDs for new entries
    } catch (error) {
      console.error("Error saving TDS data:", error)
      toast.error("Failed to save TDS data.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const taxComputation = async (totalAmount) => {
    try {
      const pan = localStorage.getItem("PAN_NO");
      const year = localStorage.getItem("YEAR");

      // Fetch both regimes in parallel
      const [oldRes, newRes] = await Promise.all([
        axios.get(`${config.apiUrl}/user/tax-computation/${pan}/${year}/OLD`),
        axios.get(`${config.apiUrl}/user/tax-computation/${pan}/${year}/NEW`)
      ]);

      const prepareRegimeData = (data, regime, amount) => {
        return {
          ...data,
          taxComputationId: data?.taxComputationId || null,
          regime,
          totalTCS: amount?.totalTcs || 0,
          totalTDS: amount?.totalTds || 0,
        };
      };

      // Prepare payloads inside `taxComputation` key
      const oldPayload = {
        taxComputation: prepareRegimeData(
          oldRes.data?.taxComputation,
          "OLD",
          totalAmount
        )
      };

      const newPayload = {
        taxComputation: prepareRegimeData(
          newRes.data?.taxComputation,
          "NEW",
          totalAmount
        )
      };

      // Update both regimes in parallel
      await Promise.all([
        axios.patch(`${config.apiUrl}/user/tax-computation/${pan}/${year}/OLD`, oldPayload),
        axios.patch(`${config.apiUrl}/user/tax-computation/${pan}/${year}/NEW`, newPayload)
      ]);

    } catch (error) {
      console.error("Error in taxComputation:", error);
    }
  };

  const currentColumns = columns[activeTab]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <p>Loading TDS data...</p>
      </div>
    )
  }

  return (
    <div className="w-full bg-gray-50 rounded-lg shadow-sm">
      <ToastContainer
      // position="bottom-right"
      // autoClose={5000}
      // hideProgressBar={false}
      // newestOnTop={false}
      // closeOnClick
      // rtl={false}
      // pauseOnFocusLoss
      // draggable
      // pauseOnHover
      />
      <div className="flex overflow-x-auto">
        {tabs.map((tab) => (
          <div key={tab.id} className="relative">
            <button
              className={cn(
                "px-6 py-3 text-sm font-medium whitespace-nowrap flex items-center space-x-2",
                activeTab === tab.id ? "bg-cyan-500 text-white" : "bg-gray-100 text-gray-800 hover:bg-gray-200",
              )}
              onClick={() => setActiveTab(tab.id)}
            >
              <span>{tab.label}</span>
              {/* JSON uploader icon removed from Other TDS tab */}
            </button>
          </div>
        ))}
      </div>
      
      <div className="p-4">

        {/* JSON Uploader for Other TDS tab - REMOVED */}

         {/* TDS Claim Type Selection for Other TDS tab */}
         {activeTab === 'otherTDS' && (
           <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
             <div className="flex items-center space-x-6">
               <span className="text-sm font-medium text-gray-700">TDS Claim Type:</span>
               <div className="flex items-center space-x-4">
                 <label className="flex items-center space-x-2">
                                        <input
                       type="radio"
                       name="tdsClaimType"
                       value="Self"
                       checked={tdsClaimType === "Self"}
                       onChange={(e) => handleTdsClaimTypeChange(e.target.value)}
                       className="h-4 w-4 text-cyan-500 focus:ring-cyan-500 border-gray-300"
                     />
                     <span className="text-sm text-gray-700">Self</span>
                   </label>
                   <label className="flex items-center space-x-2">
                     <input
                       type="radio"
                       name="tdsClaimType"
                       value="Others"
                       checked={tdsClaimType === "Others"}
                       onChange={(e) => handleTdsClaimTypeChange(e.target.value)}
                       className="h-4 w-4 text-cyan-500 focus:ring-cyan-500 border-gray-300"
                     />
                   <span className="text-sm text-gray-700">Others</span>
                 </label>
               </div>
             </div>
           </div>
         )}

         {/* TDS Claim Type Selection for Other TDS2 tab */}
         {activeTab === 'otherTDS2' && (
           <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
             <div className="flex items-center space-x-6">
               <span className="text-sm font-medium text-gray-700">TDS Claim Type:</span>
               <div className="flex items-center space-x-4">
                 <label className="flex items-center space-x-2">
                   <input
                     type="radio"
                     name="tdsClaimType2"
                     value="Self"
                     checked={tdsClaimType2 === "Self"}
                     onChange={(e) => handleTdsClaimType2Change(e.target.value)}
                     className="h-4 w-4 text-cyan-500 focus:ring-cyan-500 border-gray-300"
                   />
                   <span className="text-sm text-gray-700">Self</span>
                 </label>
                 <label className="flex items-center space-x-2">
                   <input
                     type="radio"
                     name="tdsClaimType2"
                     value="Others"
                     checked={tdsClaimType2 === "Others"}
                     onChange={(e) => handleTdsClaimType2Change(e.target.value)}
                     className="h-4 w-4 text-cyan-500 focus:ring-cyan-500 border-gray-300"
                   />
                   <span className="text-sm text-gray-700">Others</span>
                 </label>
               </div>
             </div>
           </div>
         )}

         {/* TCS Claim Type Selection for TCS tab */}
         {activeTab === 'tcs' && (
           <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
             <div className="flex items-center space-x-6">
               <span className="text-sm font-medium text-gray-700">TCS Claim Type:</span>
               <div className="flex items-center space-x-4">
                 <label className="flex items-center space-x-2">
                   <input
                     type="radio"
                     name="tcsClaimType"
                     value="Self"
                     checked={tcsClaimType === "Self"}
                     onChange={(e) => handleTcsClaimTypeChange(e.target.value)}
                     className="h-4 w-4 text-cyan-500 focus:ring-cyan-500 border-gray-300"
                   />
                   <span className="text-sm text-gray-700">Self</span>
                 </label>
                 <label className="flex items-center space-x-2">
                   <input
                     type="radio"
                     name="tcsClaimType"
                     value="Others"
                     checked={tcsClaimType === "Others"}
                     onChange={(e) => handleTcsClaimTypeChange(e.target.value)}
                     className="h-4 w-4 text-cyan-500 focus:ring-cyan-500 border-gray-300"
                   />
                   <span className="text-sm text-gray-700">Others</span>
                 </label>
               </div>
             </div>
           </div>
         )}
         
         <div className="overflow-x-auto">
           <table className="w-full">
                         <thead>
               <tr>
                 {currentColumns.map((col) => {
                   // Check if header should be hidden based on TDS Claim Type
                   if (activeTab === 'otherTDS' && col.conditional) {
                     if (tdsClaimType === 'Self') {
                       return null; // Hide conditional headers when "Self" is selected
                     }
                   }
                   
                   // Check if header should be hidden based on TDS Claim Type 2 for Other TDS2 tab
                   if (activeTab === 'otherTDS2' && col.conditional) {
                     if (tdsClaimType2 === 'Self') {
                       return null; // Hide conditional headers when "Self" is selected
                     }
                   }
                   
                   // Check if header should be hidden based on TCS Claim Type for TCS tab
                   if (activeTab === 'tcs' && col.conditional) {
                     if (tcsClaimType === 'Self') {
                       return null; // Hide conditional headers when "Self" is selected
                     }
                   }
                   
                   return (
                     <th key={col.id} className="px-2 py-2 text-left text-sm font-medium text-gray-700">
                       {col.label}
                     </th>
                   );
                 })}
                 <th className="w-20"></th>
               </tr>
             </thead>
            <tbody>
              {formData[activeTab].map((row) => (
                <tr key={row.id} className="border-t border-gray-200">
                  {currentColumns.map((col) => {
                                         // Check if field should be hidden based on TDS Claim Type
                     if (activeTab === 'otherTDS' && col.conditional) {
                       if (tdsClaimType === 'Self') {
                          return null; // Hide conditional fields when "Self" is selected
                       }
                     }
                     
                     // Check if field should be hidden based on TDS Claim Type 2 for Other TDS2 tab
                     if (activeTab === 'otherTDS2' && col.conditional) {
                       if (tdsClaimType2 === 'Self') {
                          return null; // Hide conditional fields when "Self" is selected
                       }
                     }
                     
                     // Check if field should be hidden based on TCS Claim Type for TCS tab
                     if (activeTab === 'tcs' && col.conditional) {
                       if (tcsClaimType === 'Self') {
                          return null; // Hide conditional fields when "Self" is selected
                       }
                     }
                    
                    return (
                      <td key={`${row.id}-${col.id}`} className="px-2 py-2">
                        {col.type === "checkbox" ? (
                          <div className="flex items-center justify-center">
                            <input
                              type="checkbox"
                              className="h-5 w-5 rounded border-gray-300 text-cyan-500 focus:ring-cyan-500"
                              checked={row[col.id] || false}
                              onChange={() => handleCheckboxChange(row.id, col.id)}
                            />
                          </div>
                                                 ) : (
                          <div className="flex items-center">
                            {col.prefix && <span className="text-gray-500 mr-1">{col.prefix}</span>}
                            {
                              col.options ? (
                                <select
                                  className="w-full border-gray-300 rounded-md p-1"
                                  value={row.fields[col.id] || ""}
                                  onChange={(e) => handleInputChange(row.id, col.id, e.target.value)}
                                >
                                  <option value="">Select {col.label}</option>
                                                                     {(optionsMap[col.id] || []).map((option) => (
                                     <option key={option.codeValue} value={option.codeValue}>
                                       {col.id === "section" ? `${option.codeValue} - ${option.description}` : option.description}
                                     </option>
                                   ))}
                                </select>
                              ) : (
                                <input
                                  type={col.id === "dateOfDeposit" ? "date" : "text"}
                                  placeholder={col.id === "dateOfDeposit" ? "Select date" : "Enter text"}
                                  value={row.fields[col.id] || ""}
                                  onChange={(e) => handleInputChange(row.id, col.id, e.target.value)}
                                  data-row-id={row.id}
                                  data-field-id={col.id}
                                                                     style={{
                                     width: "100%",
                                     borderColor: (hasError(row.id, col.id) || getFieldError(row.id, col.id) || getFieldError(row.id, col.apiField)) ? "#ef4444" : "#e5e7eb", // red-500 for error
                                     borderWidth: "1px",
                                     borderStyle: "solid",
                                     borderRadius: "0.375rem",
                                     fontSize: "0.875rem",
                                     outline: "none",
                                     padding: "0.375rem 0.75rem",
                                     boxSizing: "border-box",
                                   }}
                                   onFocus={(e) => {
                                     e.target.style.borderColor = (hasError(row.id, col.id) || getFieldError(row.id, col.id) || getFieldError(row.id, col.apiField)) ? "#ef4444" : "#06b6d4";
                                     e.target.style.boxShadow = (hasError(row.id, col.id) || getFieldError(row.id, col.id) || getFieldError(row.id, col.apiField))
                                       ? "0 0 0 3px rgba(239, 68, 68, 0.5)"
                                       : "0 0 0 3px rgba(6, 182, 212, 0.5)";
                                   }}
                                   onBlur={(e) => {
                                     e.target.style.borderColor = (hasError(row.id, col.id) || getFieldError(row.id, col.id) || getFieldError(row.id, col.apiField)) ? "#ef4444" : "#e5e7eb";
                                     e.target.style.boxShadow = "none";
                                   }}
                                />
                              )
                            }
                          </div>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-2 py-2">
                    <div className="flex space-x-1">
                      <button
                        type="button"
                        className="p-1 rounded-md bg-cyan-500 text-white hover:bg-cyan-600"
                        onClick={addRow}
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                      {formData[activeTab].length > 1 && (
                        <button
                          type="button"
                          className="p-1 rounded-md bg-red-500 text-white hover:bg-red-600"
                          onClick={() => removeRow(row.id, row)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-8 space-y-2">
          <div className="flex justify-between bg-gray-100 p-2 rounded">
            <div className="font-medium">
              Total {activeTab === "tcs" ? "TCS" : activeTab === "taxPaid" ? "Tax Paid" : "TDS"}
            </div>
            <div className="font-medium">
              ₹ {activeTab === "taxPaid"
                ? calculateTotal("amount")
                : calculateTotal(activeTab === "tcs" ? "tcs" : "tds")}
            </div>
          </div>

          {activeTab !== "taxPaid" && activeTab !== "tdsOnSalary" && (
            <>
              <div className="flex justify-between bg-gray-100 p-2 rounded">
                <div className="font-medium">
                  Total {activeTab === "tcs" ? "TCS" : "TDS"} Claimed in own hand
                </div>
                <div className="font-medium">
                  {/* ₹ {calculateTotal(activeTab === "tcs" ? "tcsClaimed" : "tdsClaimed")} */}
                  ₹ {calculateTotal("claimedByOthers")}
                </div>
              </div>

              <div className="flex justify-between bg-gray-100 p-2 rounded">
                <div className="font-medium text-sm">
                  Claimed in the hands of spouse as per section 5A or any other person
                  <br />
                  as per rule 37BA(2)(if applicable)
                </div>
                <div className="font-medium">
                  ₹ 00.00
                  {/* Placeholder for claimed by others, if applicable */}
                  {/* {calculateTotal("claimedByOthers")} */}
                </div>
              </div>
            </>
          )}
        </div>
        <div className="mt-8 flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
          <button className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button
            className="px-6 py-2 bg-cyan-500 text-white rounded-md hover:bg-cyan-600 transition-colors flex items-center justify-center space-x-2"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save Details"}
          </button>
        </div>
      </div>

             <ValidationErrorSidebar
         errors={validationErrors}
         isVisible={true}
         onClose={handleCloseErrorSidebar}
         onFieldClick={handleFieldClick}
         title={
           activeTab === "taxPaid" ? "Tax Paid Validation Errors" : 
           activeTab === "tdsOnSalary" ? "TDS on Salary Validation Errors" :
           "TDS/TCS Validation Errors"
         }
       />
    </div>
  )
}
